import { axiosPrivate } from "../api/axios";
import { useEffect } from "react";
import useRefreshToken from "./useRefreshToken";
import useAuth from "./useAuth";
import useLogout from "../hooks/useLogout";
import { useNavigate, Link } from "react-router-dom";

const useAxiosPrivate = () => {
  const refresh = useRefreshToken();
  const { auth } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();
  useEffect(() => {
    const requestIntercept = axiosPrivate.interceptors.request.use(
      (config) => {
        if (!config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${auth?.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    const responseIntercept = axiosPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Ignore canceled requests (component unmount cleanup)
        if (
          error.message === "canceled" ||
          error.name === "CanceledError" ||
          error.name === "AbortError"
        ) {
          return Promise.reject(error);
        }

        console.log("privateaxio error:", error, error?.response?.status);
        const prevRequest = error?.config;

        // Handle 403 Forbidden - try to refresh token
        if (error?.response?.status === 403 && !prevRequest?.sent) {
          prevRequest.sent = true;
          try {
            const newAccessToken = await refresh();
            if (!newAccessToken) {
              navigate("/login");
              return Promise.reject(error);
            }
            prevRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return axiosPrivate(prevRequest);
          } catch (refreshError) {
            console.log("Refresh token error", refreshError);
            navigate("/login");
            return Promise.reject(refreshError);
          }
        }

        // Handle 401 Unauthorized - session expired, force logout
        if (error?.response?.status === 401) {
          await logout();
          navigate("/login");
          return Promise.reject(error);
        }

        // Handle custom 480-499 error codes (application specific)
        if (error?.response?.status >= 480 && error?.response?.status < 500) {
          console.log(error.response.message);
        }

        return Promise.reject(error);
      },
    );

    return () => {
      axiosPrivate.interceptors.request.eject(requestIntercept);
      axiosPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [auth, refresh]);

  return axiosPrivate;
};

export default useAxiosPrivate;
